import React, { memo } from "react";
import "./AccordionSelect.scss";
import { hexToRgba } from "src/components/MultiDateTimeCalendar/calendarUtils";
import { ReactComponent as CheckFillIcon } from "../../../../images/icons/check-fill.svg";
/*
 * Web port of the mobile `accordionSelect` component. A form row that shows a
 * leading icon, its label and current value, and expands in place to reveal the
 * option list. Expansion is controlled by the parent so a group of these can
 * enforce single-open behaviour.
 */

const isSameOption = (a, b) => {
  if (a?.id != null && b?.id != null) {
    return a.id === b.id;
  }
  return a?.title === b?.title;
};

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline
      points="9 6 15 12 9 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AccordionSelect = ({
  label,
  icon,
  options = [],
  selection,
  onSelect,
  expanded = false,
  onToggle,
  disabled = false,
  required = false,
  showDivider = false,
  placeholder,
  accentColor,
}) => {
  const resolvedPlaceholder = placeholder ?? `Select ${label}`;

  const handleToggle = () => {
    if (disabled) return;
    onToggle?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const classNames = [
    "accordion-select",
    expanded && "accordion-select--expanded",
    disabled && "accordion-select--disabled",
    showDivider && "accordion-select--divided",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classNames}>
      <div
        className="accordion-select__header"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={expanded}
        aria-disabled={disabled}
        aria-label={`${label}${selection?.title ? `, ${selection.title}` : ""}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span
          className="accordion-select__icon-box"
          style={accentColor ? { "--accordion-accent": accentColor, "--accordion-accent-bg": hexToRgba(accentColor, 0.12) } : undefined}
        >
          {icon}
        </span>

        <span className="accordion-select__text">
          <span className="accordion-select__label" style={{ color: "#6b6c6d" }}>
            {label}
            {required && <span className="accordion-select__required"> *</span>}
          </span>
          <span className={`accordion-select__value ${selection?.title ? "" : "accordion-select__value--placeholder"}`}>
            {selection?.title || resolvedPlaceholder}
          </span>
        </span>

        <span className="accordion-select__chevron">
          <ChevronIcon />
        </span>
      </div>

      {expanded && (
        <ul className="accordion-select__panel" role="listbox">
          {options.length === 0 && <li className="accordion-select__empty">No options available</li>}
          {options.map((option) => {
            const selected = selection ? isSameOption(option, selection) : false;
            return (
              <li key={String(option.id ?? option.title)}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`accordion-select__option ${selected ? "accordion-select__option--selected" : ""}`}
                  onClick={() => onSelect?.(option)}
                >
                  <span className="accordion-select__option-title">{option.title}</span>
                  {selected && (
                    <span className="accordion-select__option-check">
                      <CheckFillIcon />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default memo(AccordionSelect);
